<template>
  <div>
    <div
      :id="id"
      ref="rightPanel"
      class="fixed right-0 top-[58px] z-[1501] overflow-y-auto overflow-x-hidden bg-gray-700"
      :class="{
        '!left-0': layoutStore.mainMenuAlign === 'right',
        'height-full': !topBarStore.showWindowBar && !isDragging,
        'height-full-with-bar !top-[90px]':
          topBarStore.showWindowBar && !isDragging,
        'h-[450px] rounded-md': isDragging,
      }"
      :style="{ width: width }"
    >
      <TButton
        v-if="header !== ''"
        data-testid="close-panel"
        class="!absolute right-3 top-2"
        @click="$emit('close')"
      >
        <template #icon>
          <Close />
        </template>
      </TButton>
      <div
        v-if="header !== ''"
        class="draggable box-border flex items-center gap-3 p-5"
        @dblclick="goHome"
      >
        <div
          v-if="$slots.headerIcon"
          class="box-border grid h-[40px] w-[40px] items-center justify-center rounded-md border border-solid border-gray-500"
        >
          <slot name="headerIcon" />
        </div>
        <h1
          class="block select-none font-inter text-base font-semibold leading-4 text-gray-50"
        >
          {{ header }}
        </h1>
        <a
          v-if="help"
          :href="help.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <TTip :tip="help.text" />
        </a>
      </div>
      <div
        v-if="header === ''"
        class="draggable box-border flex items-center justify-end gap-3 p-2"
        @dblclick="goHome"
      >
        <TButton data-testid="close-panel" @click="$emit('close')">
          <template #icon>
            <Close />
          </template>
        </TButton>
      </div>

      <slot />
    </div>
  </div>

  <div
    v-if="showHomePosition"
    class="height-full fixed right-0 top-[56px] z-[998] w-[304px] overflow-y-auto overflow-x-hidden bg-gray-300 opacity-25"
    :class="{
      '!left-0': layoutStore.mainMenuAlign === 'right',
      'height-full-with-bar !top-[88px]': topBarStore.showWindowBar,
    }"
  ></div>
</template>

<script setup lang="ts">
  import { useLayoutStore } from "@/stores/layoutStore";
  import { useTopBarStore } from "@/stores/topBar";
  import { IHelp } from "@/schemas/helpdesk/helpdeskSchema";
  import { Close } from "@topol/icons";
  import { TButton, TTip } from "@topol/twig-ui";
  import interact from "interactjs";
  import { ref, onMounted, onUnmounted, watch } from "vue";
  import { onClickOutside } from "@vueuse/core";

  const layoutStore = useLayoutStore();
  const topBarStore = useTopBarStore();

  const isDragging = ref(false);
  const showHomePosition = ref(false);
  const position = ref({ x: 0, y: 0 });
  const rightPanel = ref();

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    header: {
      type: String,
      default: "",
    },
    draggable: {
      type: Boolean,
      default: true,
    },
    help: {
      type: Object as () => IHelp,
      default: undefined,
    },
    width: {
      type: String,
      default: "304px",
    },
    closeOutside: {
      type: Boolean,
      default: true,
    },
  });

  const emits = defineEmits(["close", "dragging", "resizing"]);

  function goHome() {
    const element = document.getElementById(props.id);
    if (props.draggable && element) {
      position.value.x = 0;
      position.value.y = 0;
      element.style.transform = "";
      element.style.width = "";
      element.style.height = "";
      isDragging.value = false;
      showHomePosition.value = false;
      emits("dragging", false);
    }
  }

  onClickOutside(
    rightPanel,
    (event) => {
      if (
        props.closeOutside &&
        !(event.target as HTMLElement).closest(".noClosePanel")
      ) {
        emits("close");
      }
    },
    { ignore: [".tippy-box", ".tippy-box *"] }
  );

  watch(isDragging, (newVal) => {
    const element = document.getElementById(props.id);
    if (newVal && element) {
      interact(`#${props.id}`).resizable({
        edges: { bottom: true, right: true },
        listeners: {
          move: function (event) {
            position.value.x += event.deltaRect.right;
            position.value.y += event.deltaRect.top;

            element.style.width = `${event.rect.width}px`;
            element.style.height = `${event.rect.height}px`;
            element.style.transform = `translate(${position.value.x}px, ${position.value.y}px)`;

            window.$event.emit("resizingPanel");
          },
          end() {
            window.$event.emit("resizingPanel");
          },
        },
      });
    }

    if (!newVal && element) {
      interact(`#${props.id}`).unset();
    }
  });

  onMounted(() => {
    const element = document.getElementById(props.id);
    if (props.draggable && element) {
      interact(`#${props.id} .draggable`).draggable({
        listeners: {
          start() {
            isDragging.value = true;
            emits("dragging", true);
          },
          move(event) {
            position.value.x += event.dx;
            position.value.y += event.dy;

            if (position.value.y < 0) {
              position.value.y = 0;
            }

            if (position.value.x > 100) {
              position.value.x = 100;
            }

            if (position.value.x < -window.innerWidth + 250) {
              position.value.x = -window.innerWidth + 250;
            }

            if (position.value.y > window.innerHeight - 250) {
              position.value.y = window.innerHeight - 250;
            }

            if (position.value.x > -150 && position.value.y < 150) {
              showHomePosition.value = true;
            } else if (showHomePosition.value) {
              showHomePosition.value = false;
            }

            element.style.transform = `translate(${position.value.x}px, ${position.value.y}px)`;
          },
          end() {
            if (position.value.x > -150 && position.value.y < 150) {
              goHome();
            }
          },
        },
      });
    }
  });

  onUnmounted(() => {
    const element = document.getElementById(props.id);
    if (element) {
      interact(`#${props.id} .draggable`).unset();
      element.style.transform = "";
      element.style.width = "";
      element.style.height = "";
      emits("dragging", false);
    }
  });
</script>

<style scoped lang="scss">
  .height-full {
    height: calc(100vh - 58px);
  }

  .height-full-with-bar {
    height: calc(100vh - 90px);
  }
</style>
